import ApiService from './ApiService';
import type { AnnotationResponse } from './AnnotationService';
import { isLoggedInUserStudyGraderSync } from '../utils/graderStudyViewOnly';
import { normalizeAnnotationId } from '../utils/annotationIdFromResponse';

const VIEWER_ACTIVITY_UNLOAD_LOGGED_KEY = '__viewerActivityUnloadLogged';

const getAuthTokenSync = (): string | null => {
  try {
    return localStorage.getItem('token') || sessionStorage.getItem('auth_token');
  } catch {
    return null;
  }
};

type ActivityContext = 'exit' | 'save';

type ActivityPayload = {
  study_id: number;
  annotation_id?: number;
  activity: 'viewer_opened' | 'annotation_saved' | 'review';
  duration: number;
  log_type: 'annotation' | 'review';
};

const getGlobalAnnotations = (): AnnotationResponse | null => {
  try {
    const data = (window as any).annotationsData as AnnotationResponse | undefined;
    if (!data) {
      return null;
    }
    return data;
  } catch {
    return null;
  }
};

const getStudyContextFromAnnotations = () => {
  const annotations = getGlobalAnnotations();

  if (!annotations || !annotations.is_success || !annotations.data || !annotations.data.length) {
    return {
      studyId: null as number | null,
      annotationId: null as number | null,
      isCompleted: false,
      study: null as any,
    };
  }

  const first = (annotations.data[0] || {}) as any;
  const study = first.study || first.study_info || null;
  const anns = first.annotations || [];

  let studyId: number | null = null;
  let annotationId: number | null = null;
  let isCompleted = false;

  if (study && typeof study.id === 'number') {
    studyId = study.id;
    const status = (study.status || '').toString().toLowerCase();
    if (status === 'completed' || status === 'complete' || status === 'reviewed') {
      isCompleted = true;
    }
  }

  if (Array.isArray(anns) && anns.length > 0) {
    const ann = anns[0];
    if (typeof ann?.id === 'number') {
      annotationId = ann.id;
    }
    const annStatus = (ann.status || '').toString().toLowerCase();
    if (annStatus === 'submitted' || annStatus === 'completed' || annStatus === 'review') {
      isCompleted = true;
    }
  }

  return { studyId, annotationId, isCompleted, study };
};

const seedTimerState = (initialSeconds: number) => {
  const safeSeconds = Number.isFinite(initialSeconds) && initialSeconds > 0 ? initialSeconds : 0;
  const state = ((window as any).viewerTimerState || {}) as {
    elapsedSeconds?: number;
    lastLoggedSeconds?: number;
  };

  (window as any).viewerTimerState = {
    ...state,
    elapsedSeconds: safeSeconds,
    lastLoggedSeconds: safeSeconds,
  };
};

const getAndUpdateDuration = (): number => {
  const state = ((window as any).viewerTimerState || {}) as {
    elapsedSeconds?: number;
    lastLoggedSeconds?: number;
  };

  const totalElapsed = Number.isFinite(state.elapsedSeconds) ? state.elapsedSeconds! : 0;
  const lastLogged = Number.isFinite(state.lastLoggedSeconds) ? state.lastLoggedSeconds! : 0;

  const duration = Math.max(0, totalElapsed - lastLogged);

  (window as any).viewerTimerState = {
    ...state,
    elapsedSeconds: totalElapsed,
    lastLoggedSeconds: totalElapsed,
  };

  return duration;
};

const resolveActivityAndLogType = (
  ctx: ActivityContext,
  isCompleted: boolean,
  isAssignee: boolean
): Pick<ActivityPayload, 'activity' | 'log_type'> => {
  // If study is completed, always use review
  if (isCompleted) {
    return { activity: 'review', log_type: 'review' };
  }

  // If logged-in user is not the assignee, use review log_type
  if (!isAssignee) {
    if (ctx === 'save') {
      return { activity: 'annotation_saved', log_type: 'review' };
    }
    // ctx === 'exit'
    return { activity: 'viewer_opened', log_type: 'review' };
  }

  // User is the assignee, use annotation log_type
  if (ctx === 'save') {
    return { activity: 'annotation_saved', log_type: 'annotation' };
  }

  // ctx === 'exit'
  return { activity: 'viewer_opened', log_type: 'annotation' };
};

class ActivityService {
  private static instance: ActivityService;
  private apiService: ReturnType<typeof ApiService.getInstance>;

  private constructor() {
    this.apiService = ApiService.getInstance();
  }

  static getInstance(): ActivityService {
    if (!ActivityService.instance) {
      ActivityService.instance = new ActivityService();
    }
    return ActivityService.instance;
  }

  /**
   * Initialize the viewer timer from existing activity logs, if any.
   * - Uses /activity with study_id and optional annotation_id as query params.
   * - Uses the first object in the response (latest entry) and its duration_in_sec
   *   to seed the timer.
   */
  async seedTimerFromHistory(): Promise<void> {
    // Always reset timer state before attempting to seed from history so we
    // don't carry over elapsed time from a previous study when the API returns
    // no data or fails.
    seedTimerState(0);

    const { studyId, annotationId } = getStudyContextFromAnnotations();

    if (!studyId) {
      console.log('[ActivityService] seedTimerFromHistory: no study_id available');
      return;
    }

    const baseUrl = this.apiService.getGradingServiceUrl();
    const endpoint = '/activity';

    const params: Record<string, string> = {
      study_id: String(studyId),
    };

    if (annotationId) {
      params.annotation_id = String(annotationId);
    }

    // console.log('[ActivityService] Fetching activity history with params:', params);

    const response = await this.apiService.get<{
      is_success: boolean;
      data?: Array<{
        duration_in_sec?: number;
        duration?: string;
      }>;
    }>(endpoint, params, baseUrl);

    if (!response.success || !response.data || !response.data.is_success) {
      console.log('[ActivityService] No previous activity history found or request failed');
      return;
    }

    const dataArray = response.data.data || [];
    if (!Array.isArray(dataArray) || dataArray.length === 0) {
      console.log('[ActivityService] Activity history empty for this study');
      return;
    }

    const first = dataArray[0] as any;

    // Prefer total time_in_sec so timer reflects full time spent so far
    const totalTimeSec = Number(first.time_in_sec ?? first.duration_in_sec ?? 0);

    if (!Number.isFinite(totalTimeSec) || totalTimeSec <= 0) {
      console.log('[ActivityService] First activity has no positive time_in_sec/duration_in_sec, skipping seed');
      return;
    }

    // console.log('[ActivityService] Seeding timer from history with time_in_sec:', totalTimeSec);
    seedTimerState(totalTimeSec);
  }

  private buildActivityPayload(
    context: ActivityContext,
    annotationIdOverride?: number
  ): ActivityPayload | null {
    const { studyId, annotationId, isCompleted } = getStudyContextFromAnnotations();
    const resolvedAnnotationId =
      normalizeAnnotationId(annotationIdOverride) ?? annotationId ?? undefined;

    if (!studyId) {
      return null;
    }

    const duration = getAndUpdateDuration();
    if (duration <= 0) {
      return null;
    }

    // Uses assignees (curr_user_id), grader_id, session isViewOnly, and study index — same as viewer review mode.
    const isAssignee = isLoggedInUserStudyGraderSync();
    const { activity, log_type } = resolveActivityAndLogType(context, isCompleted, isAssignee);

    return {
      study_id: studyId,
      activity,
      duration,
      log_type,
      ...(resolvedAnnotationId ? { annotation_id: resolvedAnnotationId } : {}),
    };
  }

  /**
   * Best-effort activity log for tab close / refresh. Uses fetch keepalive so the
   * request can outlive the page. Must be synchronous to call from beforeunload.
   */
  logActivityOnPageUnload(): void {
    if ((window as any)[VIEWER_ACTIVITY_UNLOAD_LOGGED_KEY]) {
      return;
    }

    const payload = this.buildActivityPayload('exit');
    if (!payload) {
      return;
    }

    (window as any)[VIEWER_ACTIVITY_UNLOAD_LOGGED_KEY] = true;

    const baseUrl = this.apiService.getGradingServiceUrl();
    const url = `${baseUrl.replace(/\/$/, '')}/activity/save`;
    const token = getAuthTokenSync();
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    };

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    try {
      void fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload),
        keepalive: true,
      });
    } catch (error) {
      console.warn('[ActivityService] Failed to send unload activity log:', error);
    }
  }

  async logActivity(context: ActivityContext, annotationIdOverride?: number): Promise<void> {
    const payload = this.buildActivityPayload(context, annotationIdOverride);

    if (!payload) {
      if (!getStudyContextFromAnnotations().studyId) {
        console.warn('[ActivityService] No study_id available, skipping activity log');
      } else {
        console.log('[ActivityService] duration <= 0, skipping activity log');
      }
      return;
    }

    const baseUrl = this.apiService.getGradingServiceUrl();
    const endpoint = '/activity/save';

    const response = await this.apiService.post(endpoint, payload, baseUrl);

    if (!response.success) {
      console.error('[ActivityService] Failed to log activity:', response.error);
    }
  }
}

export default ActivityService;
